import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { type QuizGenerateItem } from '@/api/ai/type';
import { type GenerateStatus } from '@/shared/features/ai-quiz-generation/types';

export const useAiQuizGeneration = (initialGenerationId?: string) => {
  const [generateStatus, setGenerateStatus] = useState<GenerateStatus>('idle');
  const [generatedItems, setGeneratedItems] = useState<QuizGenerateItem[]>([]);

  const tryCount = useRef(0);
  const generationId = useRef(initialGenerationId ?? uuidv4());

  const handleGenerateSuccess = useCallback((items: QuizGenerateItem[]) => {
    setGeneratedItems(prevItems => {
      if (prevItems.length > 0) {
        return [...items, ...prevItems];
      }
      return items;
    });
    setGenerateStatus('success');
    tryCount.current++;
  }, []);

  const handleGenerateError = useCallback(() => {
    setGenerateStatus('error');
  }, []);

  const handleGenerateStart = (isRegenerate: boolean) => {
    if (isRegenerate) {
      setGenerateStatus('regenerating');
    } else {
      setGenerateStatus('generating');
    }
  };

  const handleReset = useCallback(() => {
    setGenerateStatus('idle');
    setGeneratedItems([]);
    generationId.current = uuidv4();
  }, []);

  const handleDeleteItem = useCallback((item: QuizGenerateItem) => {
    setGeneratedItems(prevItems => prevItems.filter(i => i !== item));
  }, []);

  return {
    generateStatus,
    generatedItems,
    tryCount,
    generationId,
    handleGenerateSuccess,
    handleGenerateError,
    handleGenerateStart,
    handleReset,
    handleDeleteItem,
  };
};
